/**
 * EmptyState — placeholder for empty lists, no-results, and error states.
 *
 * Three illustration variants so /trips, /messages, /explore-no-results don't
 * all render the same flat icon-in-a-disc.
 *
 *   - `aurora`  (default) soft amber halo behind a line icon. Compact, neutral.
 *   - `bloom`   elevated branded icon tile for screen-scale empty states.
 *   - `minimal` icon only, no background. Inline/sub-screen empty states
 *               where a big illustration would feel pompous.
 */

import React from 'react'
import { View, Text } from 'react-native'
import Animated from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { Search, WifiOff, Car, MessageCircle, CalendarOff } from '@/components/ui/icons'
import { Button } from './Button'
import { GlassSurface } from './GlassSurface'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, withAlpha, SPACING, RADIUS } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import { useEntrance } from '@/lib/hooks/use-entrance'

// ─── Types ──────────────────────────────────────────────────────────────────

type EmptyStateVariant = 'aurora' | 'bloom' | 'minimal'

/**
 * `center` — the screen-scale empty state: the block owns the viewport and
 * centres itself in it.
 * `start` — the section-scale empty state: the block sits under a left-aligned
 * section header (TUS CUENTAS, HISTORIAL…) and must share its left edge. It
 * also drops its own outer padding so the section's gap is the only spacing,
 * instead of stacking 48pt of illustration padding on top of it.
 */
type EmptyStateAlign = 'center' | 'start'

interface EmptyStateProps {
  illustration?: React.ReactNode
  title: string
  description?: string
  action?: {
    label: string
    onPress: () => void
    variant?: 'primary' | 'secondary' | 'text'
  }
  secondaryAction?: {
    label: string
    onPress: () => void
  }
  compact?: boolean
  variant?: EmptyStateVariant
  align?: EmptyStateAlign
  /**
   * Para poder AFIRMAR este estado, no solo verlo.
   *
   * Un cartel honesto que ninguna prueba puede nombrar se borra sin que nada se
   * ponga rojo. Con testID, una prueba puede exigir que la pantalla diga la
   * verdad en vez de conformarse con "no esta en blanco" -- que es como las
   * rutas de /trips pasaban mostrando un error.
   */
  testID?: string
  /**
   * Este estado significa "no pude mostrar lo que esta pantalla existe para
   * mostrar" -- una consulta que fallo, o una entidad que no aparecio.
   *
   * NO es lo mismo que un vacio legitimo: "todavia no tenes viajes" ES el
   * contenido correcto de /trips y no lleva esta marca.
   *
   * POR QUE HACE FALTA UNA MARCA. `screen-matrix` comprobaba este estado
   * buscando la CADENA "No pudimos cargar esto". Eso persigue redacciones: en
   * cuanto una pantalla escribio "Esta reserva ya no esta disponible", volvio a
   * pasar en verde mostrando su muro. Clasificar por texto es clasificar por
   * accidente; la marca clasifica por estado.
   */
  unavailable?: boolean
}

// ─── Illustration shells ────────────────────────────────────────────────────

function AuroraShell({ children, compact }: { children: React.ReactNode; compact: boolean }) {
  const { isDark } = useTheme()
  const size = compact ? 64 : 80
  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <View
        style={{
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: size / 2,
          // A 6% amber wash reads as a halo on the dark background (1.11:1)
          // and as nothing at all on the light one (1.01:1) — the notifications
          // empty state was a bare crossed-bell glyph floating in a void. Light
          // needs a heavier wash to reach the same lift, because it is tinting
          // an almost-white page instead of a near-black one; at 18% it lands
          // on the same 1.11:1 the dark halo already had.
          backgroundColor: withAlpha(COLORS.amber.DEFAULT, isDark ? 0.06 : 0.18),
        }}
      />
      {children}
    </View>
  )
}

function BloomShell({ children, compact }: { children: React.ReactNode; compact: boolean }) {
  const { isDark } = useTheme()
  const size = compact ? 76 : 104
  const iconFrame = compact ? 54 : 70

  return (
    <GlassSurface
      tone="regular"
      style={{
        width: size,
        height: size,
      }}
      radius={compact ? RADIUS.lg : RADIUS.xl}
      contentStyle={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <View
        style={{
          width: iconFrame,
          height: iconFrame,
          borderRadius: compact ? RADIUS.md : RADIUS.lg,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isDark ? 'rgba(240,66,45,0.18)' : 'rgba(240,66,45,0.10)',
        }}
      >
        {children}
      </View>
    </GlassSurface>
  )
}

function MinimalShell({ children }: { children: React.ReactNode }) {
  return <View style={{ alignItems: 'center', justifyContent: 'center' }}>{children}</View>
}

// ─── Base EmptyState ────────────────────────────────────────────────────────

function EmptyStateBase({
  illustration,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
  variant = 'aurora',
  align = 'center',
  testID,
  unavailable = false,
}: EmptyStateProps) {
  const enter = useEntrance()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const inline = align === 'start'
  const textAlign = inline ? 'left' : 'center'

  const ShellFor = (node: React.ReactNode) => {
    if (variant === 'bloom') return <BloomShell compact={compact}>{node}</BloomShell>
    if (variant === 'minimal') return <MinimalShell>{node}</MinimalShell>
    return <AuroraShell compact={compact}>{node}</AuroraShell>
  }

  return (
    <Animated.View
      collapsable={false}
      testID={testID}
      entering={enter()}
      style={{
        alignItems: inline ? 'flex-start' : 'center',
        justifyContent: 'center',
        paddingHorizontal: inline ? 0 : compact ? SPACING.md : SPACING.xl,
        paddingVertical: inline ? 0 : compact ? SPACING.lg : SPACING['2xl'],
      }}
    >
      {/*
        Marcador de estado, no de texto. Mide 0x0 y no se ve; existe para que una
        prueba pueda preguntar "esta pantalla mostro su contenido?" sin depender
        de como este redactado el muro.
      */}
      {unavailable && <View testID="screen-state.unavailable" style={{ width: 0, height: 0 }} />}
      {illustration ? (
        <View
          style={{
            marginBottom: variant === 'minimal' ? SPACING.sm : compact ? SPACING.sm : SPACING.lg,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {ShellFor(illustration)}
        </View>
      ) : null}
      <Text
        style={{
          fontSize: compact ? 16 : 20,
          color: colors.textPrimary,
          fontFamily: FONTS.bodyBold,
          textAlign,
          marginBottom: SPACING.xs,
        }}
      >
        {title}
      </Text>
      {description ? (
        <Text
          style={{
            fontSize: compact ? 13 : 15,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            textAlign,
            lineHeight: compact ? 18 : 22,
            maxWidth: 300,
          }}
        >
          {description}
        </Text>
      ) : null}
      {action && (
        <View style={{ marginTop: SPACING.lg }}>
          <Button
            title={action.label}
            onPress={action.onPress}
            variant={action.variant ?? 'primary'}
            size="md"
          />
        </View>
      )}
      {secondaryAction && (
        <View style={{ marginTop: SPACING.xs }}>
          <Button
            title={secondaryAction.label}
            onPress={secondaryAction.onPress}
            variant="text"
            size="sm"
          />
        </View>
      )}
    </Animated.View>
  )
}

// ─── Pre-built variants ─────────────────────────────────────────────────────
// Screen-scale variants use the branded icon tile. Inline helpers stay subtle.

function NoVehicles({ onAddVehicle }: { onAddVehicle?: () => void }) {
  const { t } = useTranslation()
  return (
    <EmptyStateBase
      variant="bloom"
      illustration={<Car size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
      title={t('empty.no_vehicles_title')}
      description={t('empty.no_vehicles_desc')}
      action={
        onAddVehicle ? { label: t('empty.no_vehicles_action'), onPress: onAddVehicle } : undefined
      }
    />
  )
}

function NoBookings({ onBrowse }: { onBrowse?: () => void }) {
  const { t } = useTranslation()
  return (
    <EmptyStateBase
      variant="bloom"
      illustration={<CalendarOff size={36} color={COLORS.purple.DEFAULT} strokeWidth={1.6} />}
      title={t('empty.no_bookings_title')}
      description={t('empty.no_bookings_desc')}
      action={onBrowse ? { label: t('empty.no_bookings_action'), onPress: onBrowse } : undefined}
    />
  )
}

function NoMessages() {
  const { t } = useTranslation()
  return (
    <EmptyStateBase
      variant="bloom"
      illustration={<MessageCircle size={36} color={COLORS.amber.DEFAULT} strokeWidth={1.6} />}
      title={t('empty.no_messages_title')}
      description={t('empty.no_messages_desc')}
    />
  )
}

function SearchNoResults({ query }: { query?: string }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <EmptyStateBase
      variant="aurora"
      illustration={<Search size={40} color={colors.textMuted} strokeWidth={1.4} />}
      title={t('empty.no_results_title')}
      description={
        query ? t('empty.no_results_desc_generic', { query }) : t('empty.no_results_desc_generic')
      }
    />
  )
}

function OfflineError({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <EmptyStateBase
      variant="minimal"
      illustration={<WifiOff size={40} color={colors.textMuted} strokeWidth={1.4} />}
      title={t('empty.offline_title')}
      description={t('empty.offline_desc')}
      action={onRetry ? { label: t('empty.offline_action'), onPress: onRetry } : undefined}
    />
  )
}

// ─── Export compound component ──────────────────────────────────────────────

export const EmptyState = Object.assign(EmptyStateBase, {
  NoVehicles,
  NoBookings,
  NoMessages,
  SearchNoResults,
  OfflineError,
})
